/**
 * DataTable — a compact comparison table (a location's departments, its top
 * services), and the table look Reports shares (`dataTableStyles`).
 *
 * Design system (packages/ui theme/metrics.ts `dataTableSpec`, textRoles
 * `compactSpec.table`): header cells in the `label` role on the header `band`
 * with a 2px `borderStrong` rule, like a section band; body cells `body` in
 * `text.primary`, figures tabular, `borderSubtle` rules; the table sits on the
 * page ground `bg` in a bordered, rounded box that scrolls sideways when it
 * must (the page never does). The app has the same piece and props in
 * apps/mobile/src/components/common/ds.
 *
 * For a table whose rows are units of their own (several lines each, like the
 * Accounts dashboard's accounts), `unitRowStyle` / `bandRowStyle` rule each
 * row off with a firm `borderStrong` line instead of the hairline (web only).
 *
 * A long list (the Work orders list) can also sort by a column (`sortable`
 * columns with `sort` / `onSort`: the header becomes a button with the shared
 * SortIcon and `aria-sort`), open a row (`onRowClick`: the row is a link-like
 * target with the `wash` hover, Enter opens it from the keyboard) and edge a
 * row that needs attention in amber (`rowTone` `caution`, as ListRow's).
 *
 * `CellLink` is a count in a cell that opens what it counts ("2" late cars):
 * `textAccent`, underlined; it grows into the cell's padding so it is a 36px
 * target on a desktop and 44px on a phone without making rows taller. Plain
 * text without `onOpen`, muted at 0.
 */
import React from 'react';
import { compactSpec, compactVars, dataTableSpec, textRole, tokenVars } from '@turn-connect/ui';
import { SortIcon } from './SortIcon';

const CELL_PADDING = `${compactSpec.table.cellPaddingVertical}px ${compactSpec.table.cellPaddingHorizontal}px`;

/** The shared table look (Reports' grids read these too, via reportStyles). */
export const dataTableStyles = {
  /** A box a table scrolls inside: `bg` ground, `border`, rounded. */
  scroll: {
    overflowX: 'auto',
    maxWidth: '100%',
    minWidth: 0,
    border: `1px solid ${tokenVars.border}`,
    borderRadius: dataTableSpec.radius,
    background: tokenVars.bg,
  } as React.CSSProperties,
  /** A header cell: the `label` role on the header `band` with a firm rule. */
  th: {
    ...textRole('label'),
    padding: CELL_PADDING,
    textAlign: 'left',
    background: tokenVars.band,
    borderBottom: `${dataTableSpec.ruleWidth}px solid ${tokenVars.borderStrong}`,
    whiteSpace: 'nowrap',
    userSelect: 'none',
  } as React.CSSProperties,
  /** A body cell: `body` text in `text.primary`, figures line up. */
  td: {
    ...textRole('body', tokenVars.text.primary),
    padding: CELL_PADDING,
    borderBottom: `1px solid ${tokenVars.borderSubtle}`,
    fontVariantNumeric: 'tabular-nums',
  } as React.CSSProperties,
} as const;

/** A totals row cell: bold figures on the header band's ground. */
export const totalCellStyle: React.CSSProperties = {
  ...dataTableStyles.td,
  fontWeight: 700,
  background: tokenVars.band,
  borderTop: `${dataTableSpec.ruleWidth}px solid ${tokenVars.borderStrong}`,
};

/** Zebra band for every other row (on the table's `bg`). */
export const zebra = (i: number): string => (i % 2 === 1 ? tokenVars.wash : 'transparent');

/**
 * The rule under a row that is a unit of its own (unitRowStyle, bandRowStyle).
 * Firmer than the `borderSubtle` hairline between plain table cells.
 */
export const unitRowRule = `1px solid ${tokenVars.borderStrong}`;

/**
 * Opt-in, for tables whose rows are units of their own: several lines each
 * (an account's logo, name, status and figures), where hairlines let them run
 * together. A firm rule under each row and the `wash` hover tint; no zebra,
 * so the row's accent figures keep AA on its ground in both themes (the
 * zebra wash takes `textAccent` under 4.5:1 in dark). DataTable's own rows and
 * Reports keep the `borderSubtle` hairline.
 */
export const unitRowStyle = (hovered = false): React.CSSProperties => ({
  background: hovered ? tokenVars.wash : 'transparent',
  borderBottom: unitRowRule,
});

/**
 * A band row among unit rows (a location group's subtotal, which opens its
 * locations): the header `band` ground, `bandHover` under the pointer, and the
 * same rule, so it reads as a heading over the rows under it.
 */
export const bandRowStyle = (hovered = false): React.CSSProperties => ({
  background: hovered ? tokenVars.bandHover : tokenVars.band,
  borderBottom: unitRowRule,
});

export interface DataTableColumn<R> {
  key: string;
  /** Header text (drawn in the `label` role). */
  label: string;
  /** `end` for figures. Default `start`. */
  align?: 'start' | 'end';
  /** Fixed width in px, cell padding included; columns without one share what is left. */
  width?: number;
  /** `strong` (600) for the figure that matters, `muted` for a supporting one. */
  tone?: 'strong' | 'muted';
  /** One line, ending in an ellipsis (a long name). */
  truncate?: boolean;
  /** Wraps onto more lines instead (a name that must stay readable on a phone, beside fixed figure columns); its header label may wrap too. */
  wrap?: boolean;
  /** What a screen reader says instead of the cell's shorthand ("Average 0.6 days, no target" for "0.6 d / —"). */
  spoken?: (row: R) => string;
  /** The header sorts by this column (with the table's `sort` / `onSort`). */
  sortable?: boolean;
  render: (row: R) => React.ReactNode;
}

export type DataTableSortDirection = 'asc' | 'desc';

export interface DataTableProps<R> {
  columns: readonly DataTableColumn<R>[];
  rows: readonly R[];
  rowKey: (row: R) => string;
  /** The table's accessible name ("Departments"). */
  label: string;
  /** Test id of each row. */
  rowTestId?: (row: R) => string | undefined;
  /** Leave the header band out (a short list whose section title says it all). */
  hideHeader?: boolean;
  testId?: string;
  /** The column the rows are sorted by (its header shows the direction). */
  sort?: { key: string; direction: DataTableSortDirection } | null;
  /** A sortable header was pressed: a new column starts ascending, the sorted one flips. */
  onSort?: (key: string, direction: DataTableSortDirection) => void;
  /** Opens a row (a click anywhere on it, or Enter on the focused row). Controls inside it stop their own clicks. */
  onRowClick?: (row: R) => void;
  /** `caution` edges the row in amber (it needs attention); the words in it still say why. */
  rowTone?: (row: R) => 'caution' | null | undefined;
  /** A row's spoken name while it is a target (`onRowClick`). */
  rowLabel?: (row: R) => string;
  /** Shown across the table when there are no rows. */
  emptyMessage?: React.ReactNode;
}

const toneStyle = (tone: DataTableColumn<unknown>['tone']): React.CSSProperties | undefined =>
  tone === 'strong' ? { fontWeight: 600 } : tone === 'muted' ? { color: tokenVars.text.muted } : undefined;

/** Read by screen readers, not drawn. */
const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

/** Room a column without a width keeps before the table scrolls sideways instead. */
const FLEX_COLUMN_MIN = 96;

/** Hover and keyboard focus for rows that open and for sort headers (inline styles cannot express them). */
const ROW_CSS = `.tc-dt-row:hover > td { background: ${tokenVars.wash}; }
.tc-dt-row:focus-visible { outline: 2px solid ${tokenVars.focus}; outline-offset: -2px; }
.tc-dt-sort:focus-visible { outline: 2px solid ${tokenVars.focus}; outline-offset: 2px; border-radius: 4px; }
.tc-dt-sort:hover { color: ${tokenVars.text.primary}; }`;

const sortButton: React.CSSProperties = {
  font: 'inherit',
  color: 'inherit',
  letterSpacing: 'inherit',
  textTransform: 'inherit',
  background: 'none',
  border: 0,
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  // At least a 24px target (WCAG 2.5.8) without a taller header.
  minHeight: 24,
  maxWidth: '100%',
  whiteSpace: 'nowrap',
};

export function DataTable<R>({
  columns,
  rows,
  rowKey,
  label,
  rowTestId,
  hideHeader = false,
  testId,
  sort,
  onSort,
  onRowClick,
  rowTone,
  rowLabel,
  emptyMessage,
}: DataTableProps<R>) {
  // Fixed layout gives the flexible columns only what is left: on a narrow
  // screen (320px, or zoomed) that can be nothing, so the table keeps a
  // minimum width and its box scrolls sideways.
  const minWidth = columns.reduce((sum, c) => sum + (c.width ?? FLEX_COLUMN_MIN), 0);
  return (
    <div style={dataTableStyles.scroll} data-testid={testId}>
      {(onRowClick || onSort) && <style>{ROW_CSS}</style>}
      <table aria-label={label} style={{ width: '100%', minWidth, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        {!hideHeader && (
          <thead>
            <tr>
              {columns.map((c) => {
                const sorted = sort?.key === c.key;
                const canSort = !!(c.sortable && onSort);
                return (
                  <th
                    key={c.key}
                    scope="col"
                    title={c.label}
                    aria-sort={sorted ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                    data-column-key={c.key}
                    style={{
                      ...dataTableStyles.th,
                      textAlign: c.align === 'end' ? 'right' : 'left',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      // A column whose cells wrap may wrap its label too, rather than cut it.
                      ...(c.wrap ? { whiteSpace: 'normal', verticalAlign: 'bottom' } : null),
                      ...(sorted ? { color: tokenVars.text.primary } : null),
                    }}
                  >
                    {canSort ? (
                      <button
                        type="button"
                        className="tc-dt-sort"
                        onClick={() => onSort!(c.key, sorted && sort!.direction === 'asc' ? 'desc' : 'asc')}
                        data-testid={testId ? `${testId}-sort-${c.key}` : undefined}
                        style={c.wrap ? { ...sortButton, whiteSpace: 'normal', textAlign: 'left' } : sortButton}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span>
                        <SortIcon active={sorted} direction={sorted ? sort!.direction : 'asc'} />
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.length === 0 && emptyMessage !== undefined && (
            <tr>
              <td colSpan={columns.length} style={{ ...dataTableStyles.td, borderBottom: 0, padding: '24px 12px', textAlign: 'center' }}>
                <span style={textRole('body')}>{emptyMessage}</span>
              </td>
            </tr>
          )}
          {rows.map((r, i) => {
            const tone = rowTone?.(r) || undefined;
            return (
              <tr
                key={rowKey(r)}
                data-testid={rowTestId?.(r)}
                data-tone={tone}
                className={onRowClick ? 'tc-dt-row' : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                aria-label={onRowClick ? rowLabel?.(r) : undefined}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        if (e.target !== e.currentTarget || e.key !== 'Enter') return;
                        e.preventDefault();
                        onRowClick(r);
                      }
                    : undefined
                }
                style={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {columns.map((c, ci) => (
                  <td
                    key={c.key}
                    data-column-key={c.key}
                    style={{
                      ...dataTableStyles.td,
                      ...toneStyle(c.tone),
                      textAlign: c.align === 'end' ? 'right' : 'left',
                      ...(i === rows.length - 1 ? { borderBottom: 0 } : null),
                      // The attention edge, inside the first cell so the table's own box stays square.
                      ...(tone === 'caution' && ci === 0 ? { boxShadow: `inset 4px 0 0 ${tokenVars.warning}` } : null),
                      ...(c.wrap
                        ? { whiteSpace: 'normal', overflowWrap: 'break-word', hyphens: 'auto', overflow: 'hidden' }
                        : c.truncate
                          ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                          : { whiteSpace: 'nowrap' }),
                    }}
                  >
                    {c.spoken ? (
                      <>
                        <span aria-hidden="true">{c.render(r)}</span>
                        <span style={visuallyHidden}>{c.spoken(r)}</span>
                      </>
                    ) : (
                      c.render(r)
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export interface CellLinkProps {
  /** The figure shown. */
  count: number;
  /** What opening it shows, spoken and on hover ("Show the 2 past due in Photography"). */
  label: string;
  /** Opens what it counts. Without it (or at 0) the figure is plain text. */
  onOpen?: () => void;
  testId?: string;
}

const COUNT_TEXT: React.CSSProperties = { ...textRole('body', tokenVars.textAccent), fontWeight: 600, fontVariantNumeric: 'tabular-nums' };

/**
 * A count in a table cell that opens what it counts (late cars): a
 * `textAccent` underlined link while there is something to open, the same
 * figure as plain attention text without `onOpen`, and muted at 0.
 */
export const CellLink: React.FC<CellLinkProps> = ({ count, label, onOpen, testId }) => {
  const text = count.toLocaleString('en-US');
  if (count <= 0) return <span style={{ ...COUNT_TEXT, color: tokenVars.text.muted, fontWeight: 400 }}>{text}</span>;
  if (!onOpen) return <span style={COUNT_TEXT}>{text}</span>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      title={label}
      data-testid={testId}
      style={{
        ...COUNT_TEXT,
        fontFamily: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        minHeight: compactVars.controlHeight,
        minWidth: 24,
        // Into the cell's padding: a full-height target without a taller row.
        margin: `-${compactSpec.table.cellPaddingVertical}px 0`,
        padding: 0,
        background: 'none',
        border: 0,
        cursor: 'pointer',
        textDecoration: 'underline',
        textUnderlineOffset: 3,
      }}
    >
      {text}
    </button>
  );
};

export default DataTable;
